/**
 * Regulatory Reporting · Reporting Dashboard
 *
 * "Mini FSCA IRS" landing screen.
 * Shows: 4 status tiles → 12-section OMNI-Risk readiness heatmap → activity feed.
 * Pulls its own data from GET /api/regulatory/omni/periods/{id}/dashboard
 * and lazily creates a period if none exists for the current quarter.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { format, formatDistanceToNow } from "date-fns";
import {
  Compass, Radio, ShieldCheck, Database, ChevronRight, RefreshCw,
  AlertTriangle, CheckCircle2, Circle, Loader2, Clock, User,
  ArrowUpRight, FileCheck2, Download,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const TONE = {
  navy:    { bg: "#EEF2FF", fg: "#0A1F44", ring: "#0A1F44" },
  emerald: { bg: "#D1FAE5", fg: "#065F46", ring: "#059669" },
  amber:   { bg: "#FEF3C7", fg: "#92400E", ring: "#D97706" },
  rose:    { bg: "#FFE4E6", fg: "#9F1239", ring: "#E11D48" },
  slate:   { bg: "#F1F5F9", fg: "#475569", ring: "#94A3B8" },
};

const STATUS_ICON = {
  ready:       { Icon: CheckCircle2, colour: "#059669" },
  in_progress: { Icon: Loader2,      colour: "#0369A1" },
  warning:     { Icon: AlertTriangle,colour: "#D97706" },
  blocked:     { Icon: AlertTriangle,colour: "#E11D48" },
  not_started: { Icon: Circle,       colour: "#94A3B8" },
};

const SOURCE_CHIP = {
  native:    { label: "Altara native",   bg: "#D1FAE5", fg: "#065F46" },
  connector: { label: "Data connector",  bg: "#DBEAFE", fg: "#1E40AF" },
  manual:    { label: "Manual entry",    bg: "#FEF3C7", fg: "#92400E" },
  unsourced: { label: "Awaiting connector", bg: "#F1F5F9", fg: "#64748B" },
};

export default function ReportingDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      // Ensure at least one period exists for this tenant
      const list = await api.get("/regulatory/omni/periods");
      let period = list.data[0];
      if (!period) {
        const now = new Date();
        const q = Math.floor(now.getMonth() / 3);
        const start = new Date(now.getFullYear(), q * 3, 1).toISOString();
        const end = new Date(now.getFullYear(), q * 3 + 3, 0).toISOString();
        const created = await api.post("/regulatory/omni/periods", {
          reporting_period_start: start,
          reporting_period_end: end,
        });
        period = created.data;
      }
      const res = await api.get(`/regulatory/omni/periods/${period.period_id}/dashboard`);
      setDashboard(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const overallPct = useMemo(() => {
    if (!dashboard?.sections?.length) return 0;
    const total = dashboard.sections.reduce((s, x) => s + x.total_fields, 0);
    const ready = dashboard.sections.reduce((s, x) => s + x.ready_fields, 0);
    return total ? Math.round((ready / total) * 1000) / 10 : 0;
  }, [dashboard]);

  if (loading) return <PageState msg="Loading Reporting Dashboard…" />;
  if (error)   return <PageState msg={error} tone="rose" />;

  const { period, tiles, sections, activity } = dashboard;

  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }} data-testid="regulatory-dashboard">
      <Hero
        period={period}
        overallPct={overallPct}
        refreshing={refreshing}
        onRefresh={() => { setRefreshing(true); load(); }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 pb-16 space-y-8">
        {/* Status tiles */}
        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="dashboard-tiles">
          {tiles.map((t) => <Tile key={t.label} tile={t} />)}
        </section>

        {/* 12-section heatmap + activity */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <SectionHeatmap sections={sections} period={period} />
          <ActivityFeed activity={activity} />
        </div>
      </div>
    </div>
  );
}

/* ─── Hero ─────────────────────────────────────────────── */

function Hero({ period, overallPct, refreshing, onRefresh }) {
  const start = period ? new Date(period.reporting_period_start) : null;
  const end   = period ? new Date(period.reporting_period_end)   : null;
  const due   = period ? new Date(period.submission_due)         : null;
  return (
    <header
      className="relative overflow-hidden text-white"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #061633 65%, #020814 100%)` }}
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{ background: `${GOLD}18` }} />
      <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full" style={{ background: `${GOLD}0F` }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
              <Compass className="w-3 h-3" />
              Regulatory Reporting · FSCA · OMNI-Risk Return
            </div>
            <h1 className="font-display text-3xl lg:text-5xl font-semibold mt-3 leading-tight max-w-3xl">
              Reporting Dashboard
            </h1>
            <p className="mt-3 text-white/70 max-w-2xl leading-relaxed">
              Every field the FSCA IRS asks for — assembled from your Regulatory Data Lake,
              provenance-tracked, validated, and ready to submit.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px]">
              <PillWhite label="Reporting period" value={start && end ? `${format(start, "d MMM")} – ${format(end, "d MMM yyyy")}` : "—"} />
              <PillWhite label="Submission due"   value={due ? format(due, "d MMM yyyy") : "—"} />
              <PillWhite label="Status"           value={(period?.status || "draft").replace(/_/g, " ")} tone="gold" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onRefresh}
              data-testid="btn-refresh-dashboard"
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-semibold bg-white/10 hover:bg-white/20 transition"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh from data lake
            </button>
            <button
              type="button"
              data-testid="btn-export-package"
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-semibold text-white"
              style={{ background: GOLD }}
              title="Coming in Phase 4 · XLSX + XBRL export"
            >
              <Download className="w-3.5 h-3.5" />
              Export submission package
            </button>
          </div>
        </div>

        {/* Global completion bar */}
        <div className="mt-8 max-w-3xl">
          <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-white/60 mb-2">
            <span>Overall completion</span>
            <span className="tabular-nums text-white">{overallPct}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${overallPct}%`, background: GOLD }}
            />
          </div>
        </div>
      </div>
    </header>
  );
}

function PillWhite({ label, value, tone = "slate" }) {
  const styles = tone === "gold"
    ? { border: `1px solid ${GOLD}88`, background: `${GOLD}22`, color: "#FBEACC" }
    : { border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.06)", color: "white" };
  return (
    <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5" style={styles}>
      <span className="uppercase tracking-widest text-[9px] text-white/50">{label}</span>
      <span className="tabular-nums">{value}</span>
    </span>
  );
}

/* ─── Tiles ────────────────────────────────────────────── */

function Tile({ tile }) {
  const t = TONE[tile.tone] || TONE.slate;
  return (
    <div
      data-testid={`tile-${tile.label.toLowerCase().replace(/\s/g, "-")}`}
      className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4 lg:p-5"
      style={{ borderLeft: `4px solid ${t.ring}` }}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400">{tile.label}</div>
      <div className="mt-2 text-2xl lg:text-3xl font-display font-semibold tabular-nums" style={{ color: NAVY }}>
        {tile.value}
      </div>
      {tile.hint && (
        <div className="mt-1 text-[11.5px] text-slate-500 leading-snug">{tile.hint}</div>
      )}
    </div>
  );
}

/* ─── Section heatmap ─────────────────────────────────── */

function SectionHeatmap({ sections, period }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-200 shadow-sm">
      <header className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400">
            OMNI-Risk · 12 sections
          </div>
          <h2 className="font-display text-lg font-semibold mt-0.5" style={{ color: NAVY }}>
            Section readiness heatmap
          </h2>
        </div>
        <div className="text-[11px] text-slate-500 hidden md:block">
          Click any section to open its workspace
        </div>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
        {sections.map((s) => <SectionCard key={s.n} s={s} periodId={period.period_id} />)}
      </div>
    </section>
  );
}

function SectionCard({ s, periodId }) {
  const status = STATUS_ICON[s.status] || STATUS_ICON.not_started;
  const source = SOURCE_CHIP[s.data_source_kind] || SOURCE_CHIP.unsourced;
  const StatusIcon = status.Icon;

  const barTone =
    s.completion_pct >= 90 ? "#059669" :
    s.completion_pct >= 60 ? "#0369A1" :
    s.completion_pct >= 30 ? "#D97706" : "#94A3B8";

  return (
    <Link
      to={`/regulatory/omni/${periodId}/sections/${s.n}`}
      data-testid={`section-card-${s.n}`}
      className="group rounded-xl border border-slate-200 bg-white p-4 transition hover:border-slate-400 hover:shadow-sm block"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-8 h-8 rounded-lg text-[12px] font-mono font-semibold flex items-center justify-center text-white shrink-0"
            style={{ background: NAVY }}
          >
            {s.n}
          </span>
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold truncate" style={{ color: NAVY }}>
              {s.title}
            </div>
            <div className="text-[10.5px] text-slate-500 truncate mt-0.5 flex items-center gap-1">
              <User className="w-2.5 h-2.5" />
              {s.owner}
            </div>
          </div>
        </div>
        <StatusIcon
          className={`w-4 h-4 shrink-0 ${s.status === "in_progress" ? "animate-spin" : ""}`}
          style={{ color: status.colour }}
        />
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-[10.5px] mb-1.5">
          <span className="text-slate-400 uppercase tracking-widest">Completion</span>
          <span className="tabular-nums font-semibold" style={{ color: NAVY }}>
            {s.completion_pct}%
            {s.total_fields > 0 && (
              <span className="text-slate-400 font-normal ml-1">({s.ready_fields}/{s.total_fields})</span>
            )}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${s.completion_pct}%`, background: barTone }} />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-[10.5px] flex-wrap gap-1.5">
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold uppercase tracking-widest text-[9px]"
          style={{ background: source.bg, color: source.fg }}
        >
          <Database className="w-2.5 h-2.5" />
          {source.label}
        </span>
        <div className="flex items-center gap-2 text-slate-500">
          {s.validation_errors > 0 && (
            <span className="inline-flex items-center gap-1" style={{ color: "#E11D48" }}>
              <AlertTriangle className="w-3 h-3" />{s.validation_errors} err
            </span>
          )}
          {s.validation_warnings > 0 && (
            <span className="inline-flex items-center gap-1" style={{ color: "#D97706" }}>
              <AlertTriangle className="w-3 h-3" />{s.validation_warnings} warn
            </span>
          )}
          {s.last_refreshed && (
            <span className="text-slate-400 text-[10px] inline-flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" />{formatDistanceToNow(new Date(s.last_refreshed), { addSuffix: true })}
            </span>
          )}
        </div>
      </div>

      <div className="mt-2 text-[10.5px] text-slate-400 truncate group-hover:text-slate-600 transition">
        Source · {s.source_hint}
      </div>
    </Link>
  );
}

/* ─── Activity feed ────────────────────────────────────── */

function ActivityFeed({ activity }) {
  return (
    <aside className="rounded-2xl bg-white border border-slate-200 shadow-sm">
      <header className="px-5 py-4 border-b border-slate-100">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400">Activity</div>
        <h2 className="font-display text-lg font-semibold mt-0.5" style={{ color: NAVY }}>Recent</h2>
      </header>
      <ol className="divide-y divide-slate-100">
        {activity.map((a, i) => (
          <li key={i} className="px-5 py-3" data-testid={`activity-${i}`}>
            <div className="flex items-center gap-2 text-[10.5px] text-slate-400 mb-0.5">
              <Radio className="w-3 h-3" />
              {a.actor}
              <span className="text-slate-300">·</span>
              {formatDistanceToNow(new Date(a.at), { addSuffix: true })}
            </div>
            <div className="text-[12.5px] text-slate-800 leading-snug">
              <span className="font-semibold">{a.verb}</span>{" "}
              <span className="text-slate-500">{a.target}</span>
            </div>
            {a.detail && (
              <div className="text-[11px] text-slate-400 mt-0.5">{a.detail}</div>
            )}
          </li>
        ))}
      </ol>
      <div className="px-5 py-3 border-t border-slate-100">
        <Link to="/regulatory/audit" className="text-[11.5px] font-semibold text-slate-600 inline-flex items-center gap-1 hover:text-slate-900">
          Full audit trail <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
    </aside>
  );
}

/* ─── State fallbacks ──────────────────────────────────── */

function PageState({ msg, tone = "slate" }) {
  const t = TONE[tone] || TONE.slate;
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8F9FA" }}>
      <div className="rounded-2xl bg-white border p-8 max-w-md text-center" style={{ borderColor: t.ring }}>
        <FileCheck2 className="w-6 h-6 mx-auto" style={{ color: t.ring }} />
        <div className="mt-3 text-[13px] text-slate-700">{msg}</div>
      </div>
    </div>
  );
}
